import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Spinner } from "@heroui/react";
import { R1DataTable } from "../components/R1DataTable";
import R1FilterBar from "../components/R1FilterBar";
import { useR1DashboardContext } from "../context/R1DashboardProvider";
import {
  buildFundScopeParams,
  buildR1DownloadUrl,
  fundSelectionKey,
  r1Api,
  type R1FundSelection,
} from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";

function buildDownloadUrl(selection: R1FundSelection): string {
  const params = buildFundScopeParams(selection);
  return buildR1DownloadUrl("r1_management_companies", params);
}

type Row = Record<string, unknown>;

const columns: ColumnDef<Row>[] = [
  {
    accessorKey: "name",
    header: "Name",
    size: 220,
    cell: ({ getValue }) => {
      const v = getValue();
      return v !== null && v !== undefined ? (
        <span className="font-medium">{String(v)}</span>
      ) : (
        ""
      );
    },
  },
  {
    accessorKey: "fund_names",
    header: "Managed Funds",
    size: 380,
    cell: ({ getValue }) => {
      const v = getValue();
      if (!v) return <span className="text-slate-300 text-xs">—</span>;
      const funds = String(v).split(", ");
      return (
        <div className="flex flex-wrap gap-1 py-0.5">
          {funds.map((name, i) => (
            <span
              key={i}
              className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600"
            >
              {name}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: "country",
    header: "Country",
    size: 110,
    cell: ({ getValue }) => {
      const v = getValue();
      return v !== null && v !== undefined ? String(v) : "";
    },
  },
  {
    accessorKey: "address",
    header: "Address",
    size: 220,
    cell: ({ getValue }) => {
      const v = getValue();
      return v !== null && v !== undefined ? String(v) : "";
    },
  },
  {
    accessorKey: "contacts",
    header: "Contacts",
    size: 280,
    cell: ({ getValue }) => {
      const v = getValue();
      if (!Array.isArray(v) || v.length === 0) return "";
      const emails = (v as unknown[]).flatMap((c) => {
        if (typeof c === "string") return c ? [c] : [];
        if (
          c &&
          typeof c === "object" &&
          "email" in c &&
          typeof (c as { email?: unknown }).email === "string"
        )
          return [(c as { email: string }).email];
        return [];
      });
      if (emails.length === 0) return "";
      return (
        <div className="flex flex-col gap-0.5 py-0.5">
          {emails.map((email, i) => (
            <a
              key={i}
              href={`mailto:${email}`}
              className="truncate text-[12px] text-blue-600 hover:text-blue-800 hover:underline"
              title={email}
            >
              {email}
            </a>
          ))}
        </div>
      );
    },
  },
];

export default function ManagementCompaniesView() {
  const { selectedFundIds, fundSelection } = useR1DashboardContext();

  const scopeKey = fundSelectionKey(fundSelection);

  const { data, isLoading } = useQuery({
    queryKey: ["r1", "management-companies", scopeKey],
    queryFn: () => r1Api.getManagementCompanies(fundSelection),
    enabled: selectedFundIds.length > 0,
    staleTime: 60_000,
  });

  const rows = useMemo(() => (data?.data ?? []) as Row[], [data]);

  return (
    <div className="flex flex-col gap-4">
      <R1FilterBar hidePeriod />
      {selectedFundIds.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      ) : isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <h1 className="font-semibold text-xl text-primary">
              General Partners
            </h1>
            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
              {rows.length.toLocaleString()} companies
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
            <R1DataTable<Row, unknown>
              table_name="r1-management-companies"
              columns={columns}
              data={rows}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["name"]}
              noCard
            />
            <div className="flex items-center border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildDownloadUrl(fundSelection)}
                filename={`r1_management_companies_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
